import { Autocomplete, Box, Button, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import React from 'react';
import { User } from '../../models/user';
import UserFetch, { UserFetchJson } from '../../userFetch';
import CreateUserDialog from './CreateUserDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import useOwnerNames from '../../hooks/useOwnerNames';
import BuilderTokenDialog, { BuilderTokenInfo } from './BuilderTokenDialog';
import { onWsEvent } from '../../websocket';

type EditableField = 'email' | 'firstname' | 'lastname' | 'owner' | 'role';

function UsersPage({ isSuperAdmin, currentUserOwner }: { isSuperAdmin?: boolean; currentUserOwner?: string }) {
  const { ownerNames } = useOwnerNames();
  const [users, setUsers] = React.useState<User[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState<User | null>(null);
  const [editingCell, setEditingCell] = React.useState<{ uuid: string; field: EditableField } | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const [builderTokens, setBuilderTokens] = React.useState<BuilderTokenInfo[]>([]);
  const [builderDialogUser, setBuilderDialogUser] = React.useState<User | null>(null);
  const [builderConnected, setBuilderConnected] = React.useState(false);

  const fetchUsers = () => {
    UserFetch('/api/users')
      .then((response) => response.json())
      .then((data: User[]) => setUsers(data));
  };

  const fetchBuilderTokens = () => {
    if (!isSuperAdmin) return;
    UserFetchJson('/api/builder-tokens')
      .then((data: BuilderTokenInfo[]) => setBuilderTokens(data))
      .catch(() => {});
  };

  React.useEffect(() => {
    fetchUsers();
    fetchBuilderTokens();

    if (!isSuperAdmin) return;
    const unsub = onWsEvent('builder:status', (msg) => {
      setBuilderConnected(!!msg.connected);
      fetchBuilderTokens();
    });
    return unsub;
  }, [isSuperAdmin]);

  const builderTokenFor = (user: User): BuilderTokenInfo | null =>
    builderTokens.find(t => t.user_uuid === user.uuid) || null;

  const handleCreateUser = (userData: { email: string; password: string; firstName?: string; lastName?: string; super_admin: boolean }) => {
    UserFetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    }).then((response) => {
      if (response.ok) {
        setCreateDialogOpen(false);
        fetchUsers();
      }
    });
  };

  const handleDeleteUser = (user: User) => {
    UserFetch(`/api/users/${user.uuid}`, { method: 'DELETE' })
      .then((response) => {
        if (response.ok) {
          setDeleteConfirm(null);
          fetchUsers();
        }
      });
  };

  const handleUpdateField = (uuid: string, field: EditableField, value: string) => {
    UserFetch(`/api/users/${uuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value || null })
    }).then((response) => {
      if (response.ok) {
        setEditingCell(null);
        fetchUsers();
      }
    });
  };

  const getUserRole = (user: User) => user.super_admin ? 'super_admin' : user.tenant_admin ? 'tenant_admin' : 'user';

  const handleUpdateRole = (uuid: string, role: string) => {
    UserFetch(`/api/users/${uuid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role })
    }).then((response) => {
      if (response.ok) {
        setEditingCell(null);
        fetchUsers();
      }
    });
  };

  const startEditing = (user: User, field: EditableField) => {
    setEditingCell({ uuid: user.uuid, field });
    setEditValue(field === 'role' ? getUserRole(user) : (user[field] || ''));
  };

  const commitEdit = (uuid: string, field: EditableField) => {
    const user = users.find(u => u.uuid === uuid);
    if (user && editValue !== (user[field] || '')) {
      handleUpdateField(uuid, field, editValue);
    } else {
      setEditingCell(null);
    }
  };

  const renderEditableCell = (user: User, field: EditableField) => {
    const isEditing = editingCell?.uuid === user.uuid && editingCell?.field === field;

    if (isEditing) {
      if (field === 'role') {
        const roleLabels: Record<string, string> = { user: 'User', tenant_admin: 'Tenant Admin', super_admin: 'Super Admin' };
        return (
          <Select
            size="small"
            value={editValue}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue !== getUserRole(user)) {
                handleUpdateRole(user.uuid, newValue);
              } else {
                setEditingCell(null);
              }
            }}
            onClose={() => setEditingCell(null)}
            variant="standard"
            autoFocus
            open
            sx={{ minWidth: 120 }}
          >
            {Object.entries(roleLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        );
      }
      if (field === 'owner') {
        return (
          <Autocomplete
            options={ownerNames}
            value={editValue || null}
            onChange={(_e, value) => {
              const newValue = value || '';
              setEditValue(newValue);
              if (newValue !== (user[field] || '')) {
                handleUpdateField(user.uuid, field, newValue);
              } else {
                setEditingCell(null);
              }
            }}
            openOnFocus
            renderInput={(params) => (
              <TextField {...params} size="small" autoFocus sx={{ minWidth: 100 }} />
            )}
            onBlur={() => commitEdit(user.uuid, field)}
            size="small"
            sx={{ minWidth: 140 }}
          />
        );
      }
      return (
        <TextField
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => commitEdit(user.uuid, field)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit(user.uuid, field);
            if (e.key === 'Escape') setEditingCell(null);
          }}
          autoFocus
          sx={{ minWidth: 100 }}
        />
      );
    }

    const disabled = (field === 'owner' || field === 'role') && !isSuperAdmin;
    const roleLabels: Record<string, string> = { super_admin: 'Super Admin', tenant_admin: 'Tenant Admin', user: 'User' };
    const displayValue = field === 'role' ? roleLabels[getUserRole(user)] : (user[field] || '-');

    return (
      <Box
        onClick={() => !disabled && startEditing(user, field)}
        sx={{ cursor: disabled ? 'default' : 'pointer', minHeight: 24, '&:hover': disabled ? {} : { bgcolor: 'action.hover' }, px: 1, borderRadius: 1, color: disabled ? 'text.disabled' : undefined }}
      >
        {displayValue}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          size="small"
        >
          Add User
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Role</TableCell>
              {isSuperAdmin && <TableCell align="center">Builder</TableCell>}
              <TableCell>Date Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.uuid}>
                <TableCell>{renderEditableCell(user, 'email')}</TableCell>
                <TableCell>{renderEditableCell(user, 'firstname')}</TableCell>
                <TableCell>{renderEditableCell(user, 'lastname')}</TableCell>
                <TableCell>{renderEditableCell(user, 'owner')}</TableCell>
                <TableCell>{renderEditableCell(user, 'role')}</TableCell>
                {isSuperAdmin && (
                  <TableCell align="center">
                    {(() => {
                      const tok = builderTokenFor(user);
                      if (!user.super_admin) return <Box sx={{ color: 'text.disabled' }}>-</Box>;
                      const color = tok
                        ? (tok.revoked ? 'error' : (tok.connected && builderConnected ? 'success' : 'disabled'))
                        : 'action';
                      const title = tok
                        ? (tok.revoked ? 'Revoked' : (tok.connected && builderConnected ? 'Builder online' : 'Builder offline'))
                        : 'No token — click to generate';
                      return (
                        <Tooltip title={title}>
                          <IconButton size="small" onClick={() => setBuilderDialogUser(user)}>
                            <BuildCircleIcon color={color as any} />
                          </IconButton>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                )}
                <TableCell>{user.created ? new Date(user.created).toLocaleDateString() : '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm(user)}
                    title="Delete user"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateUser}
        isSuperAdmin={isSuperAdmin}
        currentUserOwner={currentUserOwner}
      />

      {builderDialogUser && (
        <BuilderTokenDialog
          open={!!builderDialogUser}
          userUuid={builderDialogUser.uuid}
          userEmail={builderDialogUser.email}
          existing={builderTokenFor(builderDialogUser)}
          onClose={() => setBuilderDialogUser(null)}
          onChanged={fetchBuilderTokens}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteConfirm?.email}? This will also remove all their device permissions.`}
        onConfirm={() => deleteConfirm && handleDeleteUser(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default UsersPage;
